import React, {useEffect, useState, useCallback, useRef, useLayoutEffect} from 'react'
import {useNavigation, useCurrentRoute} from 'react-navi'
import {Trans, useTranslation} from 'react-i18next'
import {useAppState} from '../../../state'

import {MainLayoutWithoutStretch, MainContent} from '../../Layout/Layout'
import {Row, InvisibleContainer} from '../../Layout/Grid'
import {GoBackHeaderTitle, HeaderRow, HeaderActions} from '../../Molecules/ViewComponents/ViewHeader'
import {P, H3} from '../../Atoms/Typography'

import {groupBy} from 'lodash'
import {Equipment, equipmentApi} from '../../../state/rest'
import {getSites} from '../../../config/utils'
import {Site} from '../../../state/state'
import {Loader} from '../../../Routes'
import {A11yAccordion} from '../../Atoms/Accordion'
import {A11yListContainer, A11yIconListItem} from '../../Atoms/Lists'
import {EmptyEquipmentList} from '../Home/NoContent'
import {Helmet} from 'react-navi-helmet-async'
import {taskGroupIconPicker} from '../../../pages/tasks/utils'
import {SharedIcon} from '../../../sharedComponents/components'

interface SiteEquipment {
  site: Site
  equipments: Equipment[]
}

const EquipmentSettingsView = () => {
  const nav = useNavigation()
  const {state} = useAppState()
  const route = useCurrentRoute()
  const {t} = useTranslation(['appliances', 'settings'])
  const chain = state.chainsById[state.selectedChainId!]
  const siteRefs = useRef<{[key: string]: HTMLDivElement | null}>({})

  const [loading, setLoading] = useState(true)
  // When returning from editing an appliance, temporarily hide the content
  // while we jump to the edited site so the user doesn't see the page render
  // at the top and then jump down.
  const [contentHidden, setContentHidden] = useState(false)

  // This layout effect handles scrolling to the edited site before paint so
  // the user doesn't see the page render at the top then jump down.
  useLayoutEffect(() => {
    const idFromRoute = route.state?.siteId as string | undefined
    const equipmentIdFromRoute = route.state?.equipmentId as string | undefined
    const idFromStorage = (() => {
      try {
        return sessionStorage.getItem('recentEditedSiteId') || undefined
      } catch (e) {
        console.error(e)
        return undefined
      }
    })()
    const equipmentIdFromStorage = (() => {
      try {
        return sessionStorage.getItem('recentEditedEquipmentId') || undefined
      } catch (e) {
        console.error(e)
        return undefined
      }
    })()

    const recentlyEditedSiteId = idFromRoute ?? idFromStorage
    const recentlyEditedEquipmentId = equipmentIdFromRoute ?? equipmentIdFromStorage

    if (!loading && recentlyEditedSiteId) {
      const key = recentlyEditedSiteId as string
      const targetElement = siteRefs.current[key]

      // Hide content while we perform the immediate jump so the user doesn't
      // see the page at the top then jump down. useLayoutEffect runs before
      // the browser paints, so setting hidden here prevents flicker.
      setContentHidden(true)

      if (targetElement) {
        try {
          targetElement?.scrollIntoView({behavior: 'auto', block: 'center'})
        } catch (e) {
          console.error(e)
        }
        try {
          ;(targetElement as HTMLElement).focus()
        } catch (e) {
          console.error(e)
        }
      }

      try {
        sessionStorage.removeItem('recentEditedSiteId')
        if (recentlyEditedEquipmentId) {
          sessionStorage.removeItem('recentEditedEquipmentId')
        }
      } catch (e) {
        console.error(e)
      }

      // Show content again — layout effect prevents a paint with the content
      // visible before we've jumped.
      setContentHidden(false)
    }
  }, [loading, route.state?.siteId, route.state?.equipmentId])

  const handleClick = (id: string, siteId?: string) => {
    // persist siteId so we can scroll back to it when returning
    try {
      if (siteId) {
        sessionStorage.setItem('recentEditedSiteId', siteId)
      }
      // persist equipment id so we can open the correct accordion on return
      sessionStorage.setItem('recentEditedEquipmentId', id)
    } catch (e) {
      console.error(e)
    }
    // also pass it in navigation state
    nav.navigate(`edit/appliance/${siteId}/${id}`, {state: {siteId, equipmentId: id}})
  }

  const [siteEquipments, setSiteEquipments] = useState<SiteEquipment[]>([])
  const setup = useCallback(async () => {
    setLoading(true)
    const sites = getSites(chain)
    // Note: This call does not update global state with the response, only this view
    const items = (await equipmentApi.getEquipmentSettings()).items || []
    const equipments = []
    for (const site of sites) {
      const filtered = items.filter(eq => site.id === eq.site?.id)
      equipments.push({site, equipments: filtered})
    }
    setSiteEquipments(equipments)
    setLoading(false)
  }, [chain])

  useEffect(() => {
    setup()
  }, [setup])

  const recentlyEditedSiteId = (() => {
    const idFromRoute = route.state?.siteId as string | undefined
    try {
      const idFromStorage = sessionStorage.getItem('recentEditedSiteId') || undefined
      return idFromRoute ?? idFromStorage
    } catch (e) {
      console.error(e)
      return idFromRoute
    }
  })()

  const recentlyEditedEquipmentId = (() => {
    const idFromRoute = route.state?.equipmentId as string | undefined
    try {
      const idFromStorage = sessionStorage.getItem('recentEditedEquipmentId') || undefined
      return idFromRoute ?? idFromStorage
    } catch (e) {
      console.error(e)
      return idFromRoute
    }
  })()

  return (
    <MainLayoutWithoutStretch>
      <Helmet title={t('common:routes.applianceSettings', 'Appliance settings')} />
      <Loader show={loading} />
      <MainContent variant="white" style={{visibility: contentHidden ? 'hidden' : 'visible'}}>
        <HeaderRow
          description={
            <Trans
              i18nKey="appliances:messages.addNew"
              defaults="Add new appliance to kitchen, and view and edit the details of existing appliances. If appliance is monitored with automatic sensor, you can edit sensor settings here."
              components={{br: <br />}}
            />
          }
        >
          <GoBackHeaderTitle
            label={t('appliances:labels.applianceSettings', 'Appliance settings')}
            path={'/settings'}
            backLabel={t('settings:labels.settingsTitle', 'Settings')}
          />
          <HeaderActions
            onConfigure={() => nav.navigate('/appliance/types')}
            onAdd={() => nav.navigate('/create/appliance')}
          />
        </HeaderRow>

        <A11yListContainer>
          {siteEquipments.length ? (
            siteEquipments.map(se => {
              const isOpen = se.site.id === recentlyEditedSiteId
              if (se.equipments.length > 0) {
                return (
                  <InvisibleContainer key={se.site.id} tabIndex={-1} ref={el => (siteRefs.current[se.site.id] = el)}>
                    <SiteEquipmentList
                      site={se.site}
                      equipments={se.equipments}
                      onEquipmentSelect={handleClick}
                      openByDefault={isOpen}
                      openEquipmentId={recentlyEditedEquipmentId}
                    />
                  </InvisibleContainer>
                )
              } else {
                return null
              }
            })
          ) : (
            <EmptyEquipmentList></EmptyEquipmentList>
          )}
        </A11yListContainer>
      </MainContent>
      {/*
      <RightSidebar>
        <p>Test</p>
      </RightSidebar>
      */}
    </MainLayoutWithoutStretch>
  )
}

interface SELProps {
  site: Site
  equipments: Equipment[]
  onEquipmentSelect: (id: string, siteId?: string) => void
  openByDefault?: boolean
  openEquipmentId?: string | undefined
}

const SiteEquipmentList = ({site, equipments, onEquipmentSelect, openByDefault = false, openEquipmentId}: SELProps) => {
  const byGroupId = groupBy(equipments, eq => eq.equipmentType!.id)
  const {t} = useTranslation('appliances')
  return (
    <A11yAccordion title={site.name} openByDefault={openByDefault}>
      {Object.keys(byGroupId).map(groupid => {
        const groupItems = byGroupId[groupid]
        const shouldOpenGroup = !!openEquipmentId && groupItems.some(eq => eq.id === openEquipmentId)
        return (
          <A11yAccordion
            disableSelectedBorder
            //storeId={null}
            offset={4}
            title={t(
              `appliances:equipmentTypes.${byGroupId[groupid][0].equipmentType?.id}`,
              byGroupId[groupid][0].equipmentType!.name!
            )}
            key={groupid}
            openByDefault={shouldOpenGroup}
          >
            {groupItems.map(eq => (
              <A11yIconListItem
                offset={5}
                onClick={() => onEquipmentSelect(eq.id, eq.site?.id)}
                key={eq.id}
                icon={<SharedIcon icon={taskGroupIconPicker(byGroupId[groupid][0].equipmentType!.icon)} />}
                content={
                  <Row justify="space-between">
                    <H3>{eq.name}</H3>
                    {eq.storageType && <P variant="small">{eq.storageType.name}</P>}
                  </Row>
                }
              ></A11yIconListItem>
            ))}
          </A11yAccordion>
        )
      })}
    </A11yAccordion>
  )
}

export default EquipmentSettingsView
